import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { ArrowDown, ArrowUp, Paperclip, Square } from 'lucide-react';

import { apiFetch, ApiError } from '../lib/api';
import { sendAndStream, type StreamController } from '../lib/streamClient';
import { getAgentConfig } from '../lib/agentConfig';
import { getScopePreference } from '../lib/scopePreference';
import { MessageBubble } from '../components/chat/MessageBubble';
import { AttachmentStrip } from '../components/chat/AttachmentStrip';
import { useImageUpload } from '../hooks/useImageUpload';

interface PersistedAttachment {
  externalId: string;
  publicUrl: string;
  mimeType?: string;
  width?: number | null;
  height?: number | null;
}

interface PersistedMessage {
  id: number;
  externalId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  tokenCount?: number | null;
  durationMs?: number | null;
  metadata?: { toolCalls?: Array<{ id?: string; name?: string; input?: unknown }> } | null;
  attachments?: PersistedAttachment[];
  createdAt: string;
}

interface SessionDetail {
  id: number;
  externalId: string;
  userId: string;
  title: string | null;
  status: string;
  messageCount: number;
  messages?: PersistedMessage[];
  approvedProposalIds?: string[];
}

interface SessionsList {
  sessions: SessionDetail[];
}

interface CostResponse {
  today: {
    inputTokens: number;
    outputTokens: number;
    estimatedCostCents: number;
    turns: number;
  };
}

interface ActiveTool {
  id?: string;
  name?: string;
  result?: unknown;
}

interface UiAttachment {
  externalId: string;
  publicUrl: string;
  width?: number | null;
  height?: number | null;
}

interface UiMessage {
  externalId: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ActiveTool[];
  attachments?: UiAttachment[];
  isStreaming?: boolean;
  durationMs?: number | null;
  tokenCount?: number | null;
}

const SCROLL_THRESHOLD = 100;

function formatCost(cents: number): string {
  if (cents < 100) return `${cents}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

export function ChatPage() {
  const agent = getAgentConfig();

  const [sessionExtId, setSessionExtId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cost, setCost] = useState<CostResponse['today'] | null>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  // Channel B (#613): tool_use ids of proposals already approved, so their
  // cards render as done rather than re-tappable.
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());

  const controllerRef = useRef<StreamController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploads,
    addImage,
    removeUpload,
    clearAll: clearUploads,
    completedAttachmentIds,
    isUploading,
  } = useImageUpload();

  const MAX_ATTACHMENTS = 5;

  // ── Initial session load ────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const list = await apiFetch<SessionsList>('/api/sessions?limit=1');
        let session = list.sessions[0];
        if (!session) {
          session = await apiFetch<SessionDetail>('/api/sessions', {
            method: 'POST',
            body: { scope: getScopePreference() },
          });
        }
        if (cancelled) return;
        setSessionExtId(session.externalId);

        const detail = await apiFetch<SessionDetail>(
          `/api/sessions/${session.externalId}`,
        );
        if (cancelled) return;

        setMessages(
          (detail.messages ?? [])
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map((m) => ({
              externalId: m.externalId,
              role: m.role as 'user' | 'assistant',
              content: m.content,
              toolCalls: m.metadata?.toolCalls?.map((t) => ({
                id: t.id,
                name: t.name,
                result: t.input,
              })),
              attachments: m.attachments?.map((a) => ({
                externalId: a.externalId,
                publicUrl: a.publicUrl,
                width: a.width ?? null,
                height: a.height ?? null,
              })),
              durationMs: m.durationMs ?? null,
              tokenCount: m.tokenCount ?? null,
            })),
        );
        setApprovedIds(new Set(detail.approvedProposalIds ?? []));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return; // login redirect
        console.error('[chat] boot failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Cost fetch ──────────────────────────────────────────────

  const refreshCost = useCallback(async () => {
    try {
      const res = await apiFetch<CostResponse>('/api/chat/cost');
      setCost(res.today);
    } catch {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    void refreshCost();
  }, [refreshCost]);

  // ── Stick-to-bottom scroll ──────────────────────────────────

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distance > SCROLL_THRESHOLD);
  }, []);

  useEffect(() => {
    // TODO (Phase 5): throttle this effect — currently fires per-token
    if (!userScrolledUp) {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, userScrolledUp]);

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setUserScrolledUp(false);
  }, []);

  // ── Send / cancel ───────────────────────────────────────────

  const handleSend = useCallback(
    async (text: string) => {
      if (!sessionExtId || busy) return;
      if (isUploading) return; // Wait for in-flight presign/PUT to finish.
      const trimmed = text.trim();
      const attachmentIds = completedAttachmentIds;
      if (!trimmed && attachmentIds.length === 0) return;

      // Snapshot the completed uploads for the optimistic user bubble so the
      // image appears immediately, before the server responds. publicUrl is
      // already live on R2 at this point (upload gates are complete).
      const sentAttachments: UiAttachment[] = uploads
        .filter((u) => u.status === 'complete' && u.attachmentId && u.publicUrl)
        .map((u) => ({
          externalId: u.attachmentId!,
          publicUrl: u.publicUrl!,
        }));

      const userMsg: UiMessage = {
        externalId: `local-${Date.now()}`,
        role: 'user',
        content: trimmed,
        attachments: sentAttachments.length > 0 ? sentAttachments : undefined,
      };
      // Placeholder assistant bubble; externalId gets replaced with the
      // real one once the server returns message_id.
      const assistantPlaceholder: UiMessage = {
        externalId: `streaming-${Date.now()}`,
        role: 'assistant',
        content: '',
        toolCalls: [],
        isStreaming: true,
      };
      setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
      setInput('');
      clearUploads();
      setBusy(true);
      setUserScrolledUp(false);

      try {
        const controller = await sendAndStream(sessionExtId, trimmed, attachmentIds, {
          onDelta: (chunk) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                copy[copy.length - 1] = {
                  ...last,
                  content: last.content + chunk,
                };
              }
              return copy;
            });
          },
          onToolStart: (data) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                const calls = [...(last.toolCalls ?? []), {
                  id: data.id,
                  name: data.name,
                }];
                copy[copy.length - 1] = { ...last, toolCalls: calls };
              }
              return copy;
            });
          },
          onToolResult: (data) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                const calls = [...(last.toolCalls ?? [])];
                // Attach result to the last tool-start that matches name + has no result yet
                for (let i = calls.length - 1; i >= 0; i--) {
                  if (
                    (calls[i].name === data.toolName || data.toolName === undefined) &&
                    calls[i].result === undefined
                  ) {
                    calls[i] = { ...calls[i], result: data.result };
                    break;
                  }
                }
                copy[copy.length - 1] = { ...last, toolCalls: calls };
              }
              return copy;
            });
          },
          onDone: (data) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                copy[copy.length - 1] = {
                  ...last,
                  externalId: data.messageId,
                  isStreaming: false,
                };
              }
              return copy;
            });
            setBusy(false);
            controllerRef.current = null;
            void refreshCost();
          },
          onCancelled: () => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                copy[copy.length - 1] = {
                  ...last,
                  content: `${last.content}\n\n[Response cancelled]`,
                  isStreaming: false,
                };
              }
              return copy;
            });
            setBusy(false);
            controllerRef.current = null;
            void refreshCost();
          },
          onError: (message) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last?.role === 'assistant' && last.isStreaming) {
                copy[copy.length - 1] = {
                  ...last,
                  content: `${last.content}\n\n*Error: ${message}*`,
                  isStreaming: false,
                };
              }
              return copy;
            });
            setBusy(false);
            controllerRef.current = null;
          },
        });
        controllerRef.current = controller;
      } catch (err) {
        console.error('[chat] send failed', err);
        setMessages((prev) => prev.slice(0, -1));
        setBusy(false);
      }
    },
    [busy, sessionExtId, refreshCost, completedAttachmentIds, uploads, clearUploads, isUploading],
  );

  // ── Attachment picking ─────────────────────────────────────

  const handleAttachTap = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      // Cap at remaining slots, not the raw max — otherwise silent client-side
      // overflow trips server's too_many_attachments on send.
      const remaining = Math.max(0, MAX_ATTACHMENTS - uploads.length);
      if (remaining === 0) {
        e.target.value = '';
        return;
      }
      const picked = Array.from(files).slice(0, remaining);
      picked.forEach((f) => void addImage(f));
      // Reset so the same file can be picked again in the next turn.
      e.target.value = '';
    },
    [addImage, uploads.length],
  );

  const handleCancel = useCallback(async () => {
    if (controllerRef.current) {
      await controllerRef.current.cancel();
    }
  }, []);

  const handleProposalApproved = useCallback((id: string) => {
    setApprovedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handleSend(input);
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col">
      {/* Cost indicator strip */}
      <div className="flex items-center justify-between border-b border-fog bg-ivory/80 px-4 py-1.5 text-[11px] text-bark">
        <span className="font-medium text-espresso">{agent.displayName}</span>
        {cost && (
          <span className="text-bark">
            {formatCost(cost.estimatedCostCents)} today · {cost.turns} turns
          </span>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative flex-1 space-y-3 overflow-y-auto px-3 py-4"
      >
        {loading && (
          <div className="flex justify-center pt-10 text-sm text-bark">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-fog border-t-gold" />
          </div>
        )}

        {!loading && messages.length === 0 && agent.greeting && (
          <div className="flex h-full items-center justify-center text-center text-sm text-bark">
            <div className="max-w-xs">
              <div className="text-base font-medium text-espresso">
                {agent.greeting}
              </div>
              <div className="mt-1 text-xs text-bark/70">
                Tap below to ask {agent.displayName} anything.
              </div>
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble
            key={m.externalId}
            role={m.role}
            content={m.content}
            toolCalls={m.toolCalls}
            attachments={m.attachments}
            isStreaming={m.isStreaming}
            durationMs={m.durationMs}
            tokenCount={m.tokenCount}
            sessionId={sessionExtId ?? undefined}
            approvedProposalIds={approvedIds}
            onProposalApproved={handleProposalApproved}
          />
        ))}

        <div ref={endRef} />
      </div>

      {/* Jump-to-bottom pill */}
      {userScrolledUp && busy && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 rounded-full bg-espresso px-3 py-1.5 text-xs text-ivory shadow-lg"
        >
          <ArrowDown className="mr-1 inline h-3 w-3" />
          New content
        </button>
      )}

      {/* Attachment strip (shows only when uploads exist) */}
      <AttachmentStrip uploads={uploads} onRemove={removeUpload} />

      {/* Hidden file input — triggered by the paperclip button below */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Input bar */}
      <form
        onSubmit={handleFormSubmit}
        className="border-t border-fog bg-ivory px-3 pb-2 pt-2"
      >
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={handleAttachTap}
            disabled={busy || uploads.length >= MAX_ATTACHMENTS}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-bark transition-opacity hover:text-espresso disabled:opacity-40"
            aria-label="Attach image"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void handleSend(input);
              }
            }}
            rows={1}
            placeholder={`Ask ${agent.displayName}…`}
            className="max-h-32 flex-1 resize-none rounded-2xl border border-fog bg-white px-3 py-2 text-sm text-espresso placeholder:text-bark/50 focus:border-gold focus:outline-none"
            disabled={busy && !controllerRef.current}
          />
          {busy ? (
            <button
              type="button"
              onClick={handleCancel}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 active:bg-red-100"
              aria-label="Cancel response"
            >
              <Square className="h-4 w-4" fill="currentColor" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={
                (!input.trim() && completedAttachmentIds.length === 0) ||
                !sessionExtId ||
                isUploading
              }
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-ivory transition-opacity disabled:opacity-40"
              aria-label="Send message"
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
